// (c) 2026 Owl Consultations. All rights reserved.
// D-SUPPORT-1 R2 — SaaS-admin session-replay panel (renders on the support ticket detail page).
// Visibility is server-enforced: replayList/replayPlayback require support.replay.view AND a
// platform (non-pharmacy-bound) actor; pharmacy users get 404 and this panel stays hidden.
// Every playback is audit-logged server-side. rrweb loads on demand (main bundle unaffected).
//
// Playback uses rrweb's own `Replayer` with a lightweight transport bar. The rrweb-player
// Svelte wrapper (2.1.0) silently fails to build its Replayer in our environment — it mounts
// the outer shell but never populates the frame (no iframe, no error), which is the "blank
// playback" bug. Driving Replayer directly (same package the recorder already uses) is reliable.
import { useEffect, useRef, useState } from 'react'
import * as api from '../../api/client'

async function gunzip(b64: string): Promise<unknown[]> {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
  const ds = new DecompressionStream('gzip')
  const stream = new Blob([bytes]).stream().pipeThrough(ds)
  const text = await new Response(stream).text()
  return JSON.parse(text) as unknown[]
}

interface ReplayerLike {
  play(timeOffset?: number): void
  pause(timeOffset?: number): void
  getCurrentTime(): number
  getMetaData(): { totalTime: number }
  on(event: string, cb: (...a: unknown[]) => void): void
  destroy(): void
  wrapper: HTMLElement
}
type MetaEvent = { type: number; data?: { width?: number; height?: number } }

const fmtTime = (ms: number) => {
  const t = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, '0')}`
}

export default function ReplayPanel({ ticketId }: { ticketId: string }) {
  const [sessions, setSessions] = useState<api.ReplaySessionMeta[] | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [offset, setOffset] = useState(0)
  const [duration, setDuration] = useState(0)

  const mountRef = useRef<HTMLDivElement | null>(null)
  const replayerRef = useRef<ReplayerLike | null>(null)
  const rafRef = useRef<number | null>(null)
  const dimsRef = useRef<{ w: number; h: number }>({ w: 1200, h: 800 })

  const stopRaf = () => { if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null } }
  const teardown = () => {
    stopRaf()
    try { replayerRef.current?.pause() } catch { /* noop */ }
    try { replayerRef.current?.destroy() } catch { /* noop */ }
    replayerRef.current = null
    if (mountRef.current) { mountRef.current.innerHTML = ''; mountRef.current.style.height = '' }
  }

  useEffect(() => {
    // 404 => not a SaaS replay viewer; hide the panel entirely.
    api.replayList(ticketId).then(r => setSessions(r.sessions)).catch(() => setSessions(null))
    return () => teardown()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId])

  // Re-fit on container resize while a session is loaded.
  useEffect(() => {
    if (!activeId) return
    const onResize = () => rescale()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId])

  const rescale = () => {
    const r = replayerRef.current, mount = mountRef.current
    if (!r || !mount) return
    const { w, h } = dimsRef.current
    const scale = Math.min(1, (mount.clientWidth || w) / w)
    r.wrapper.style.transformOrigin = 'top left'
    r.wrapper.style.transform = `scale(${scale})`
    mount.style.height = `${Math.round(h * scale)}px`
    mount.style.overflow = 'hidden'
  }

  const tick = () => {
    const r = replayerRef.current
    if (!r) return
    setOffset(Math.min(r.getCurrentTime(), duration || r.getMetaData().totalTime))
    rafRef.current = requestAnimationFrame(tick)
  }

  const load = async (id: string) => {
    setBusy(true); setError(null)
    try {
      const { batches } = await api.replayPlayback(id)
      const events = (await Promise.all(batches.map(gunzip))).flat()
      if (events.length < 2) { setError('Recording is empty or too short to play.'); return }

      const { Replayer } = await import('rrweb')
      await import('rrweb/dist/style.css')
      teardown()

      const replayer = new Replayer(events as never, {
        root: mountRef.current as HTMLElement,
        showWarning: false,
        mouseTail: false,
      }) as unknown as ReplayerLike
      replayerRef.current = replayer

      const meta = (events as MetaEvent[]).find(e => e && e.type === 4)
      dimsRef.current = { w: meta?.data?.width ?? 1200, h: meta?.data?.height ?? 800 }
      rescale()

      const total = replayer.getMetaData().totalTime
      setDuration(total); setOffset(0); setActiveId(id)
      replayer.on('finish', () => { setIsPlaying(false); stopRaf(); setOffset(total) })

      replayer.play(0)
      setIsPlaying(true)
      stopRaf(); rafRef.current = requestAnimationFrame(tick)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Playback failed')
    } finally { setBusy(false) }
  }

  const togglePlay = () => {
    const r = replayerRef.current
    if (!r) return
    if (isPlaying) { r.pause(); setIsPlaying(false); stopRaf() }
    else {
      const from = offset >= duration ? 0 : offset
      r.play(from); setIsPlaying(true)
      stopRaf(); rafRef.current = requestAnimationFrame(tick)
    }
  }

  const seek = (ms: number) => {
    const r = replayerRef.current
    if (!r) return
    setOffset(ms)
    if (isPlaying) r.play(ms); else r.pause(ms)
  }

  if (!sessions || sessions.length === 0) return null

  const label = (s: api.ReplaySessionMeta) =>
    `${new Date(s.started_at).toLocaleString()} · ${s.user ?? s.account_id.slice(0, 8)} · ${s.pharmacy ?? ''} · ${Math.round((s.duration_ms ?? 0) / 1000)}s · ${(s.event_bytes / 1024).toFixed(0)} KB`

  return (
    <section style={{ marginTop: 20, border: '1px solid var(--color-border)', borderRadius: 12, padding: 14, background: 'var(--color-surface)' }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>🎬 Session replay</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>
        Recorded by the user for this ticket. Playback is restricted to SaaS admins and every view is audit-logged.
      </div>
      {sessions.map(s => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--color-border)' }}>
          <button onClick={() => void load(s.id)} disabled={busy}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: activeId === s.id ? 'var(--color-accent, #0F766E)' : 'transparent', color: activeId === s.id ? '#fff' : 'var(--color-text)', cursor: 'pointer' }}>
            {activeId === s.id ? 'Loaded' : '▶ Play'}
          </button>
          <div style={{ fontSize: 13 }}>{label(s)}{s.user_note ? ` — “${s.user_note}”` : ''}</div>
        </div>
      ))}
      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</div>}

      {activeId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', cursor: 'pointer', minWidth: 44 }}>
            {isPlaying ? '❚❚' : '▶'}
          </button>
          <input type="range" min={0} max={duration || 1} value={Math.min(offset, duration)}
            onChange={e => seek(Number(e.target.value))} style={{ flex: 1 }} aria-label="Seek" />
          <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-mute)', minWidth: 84, textAlign: 'right' }}>
            {fmtTime(offset)} / {fmtTime(duration)}
          </span>
        </div>
      )}

      <div ref={mountRef} style={{ marginTop: 12, background: '#fff', borderRadius: 8 }} />
    </section>
  )
}
